import {api} from './api';
import {
  AIQuestion,
  AIResult,
  Biotwin,
  BiotwinCondition,
  BiotwinHistoryItem,
  BiotwinScore,
  RelevantCondition,
} from '../../features/biotwin/types';
import {identitiesApi} from './identitiesApi';
import {buildURLSearch} from '../utils/apiUtils';
import {MatchMode, OrderDirection} from '../types';

export const biotwinApi = api.injectEndpoints({
  endpoints: (builder) => ({
    checkBiotwinCalculation: builder.query<unknown, {id: number}>({
      query: ({id}) => ({url: `/biotypes/${id}/check-calculation`, method: 'GET'}),
    }),

    createBiotwin: builder.mutation<Biotwin, {name: string; tenantId: number}>({
      query: ({name, tenantId}) => ({url: `/biotypes`, method: 'POST', body: {name, tenantId}}),
      onQueryStarted: async (_arg, {dispatch, queryFulfilled}) => {
        try {
          const {data: biotwin} = await queryFulfilled;
          if (biotwin != null) {
            await dispatch(identitiesApi.endpoints.refreshToken.initiate());
            await dispatch(api.util.invalidateTags(['Biotwins']));
          }
        } catch (e) {}
      },
    }),

    deleteBiotwin: builder.mutation<Biotwin, {id: number}>({
      query: ({id}) => ({url: `/biotypes/${id}`, method: 'DELETE'}),
      onQueryStarted: async (_arg, {dispatch, queryFulfilled}) => {
        try {
          await queryFulfilled;
          await dispatch(identitiesApi.endpoints.refreshToken.initiate());
          await dispatch(api.util.invalidateTags(['Biotwins']));
        } catch (e) {}
      },
    }),

    duplicateBiotwin: builder.mutation<Biotwin, {id: number; name: string}>({
      query: ({id, name}) => ({url: `/biotypes/${id}/duplicate`, method: 'POST', body: {name}}),
      onQueryStarted: async (_arg, {dispatch, queryFulfilled}) => {
        try {
          await queryFulfilled;
          await dispatch(identitiesApi.endpoints.refreshToken.initiate());
          await dispatch(api.util.invalidateTags(['Biotwins']));
        } catch (e) {}
      },
    }),

    getBiotwin: builder.query<Biotwin, {id: number}>({
      query: ({id}) => ({url: `/biotypes/${id}`, method: 'GET'}),
      providesTags: (result) => (result != null ? [{type: 'Biotwins', id: result.id}] : []),
    }),

    getBiotwins: builder.query<
      {
        items: Biotwin[];
        totalCount: number;
      },
      {
        createdBy?: number;
        name?: string;
        nameMatchMode?: MatchMode;
        orderDirection?: OrderDirection;
        orderPropertyName?: string;
        pageNumber?: number;
        pageSize?: number;
        tenantId?: number;
      }
    >({
      query: (arg) => ({
        url: `/biotypes${buildURLSearch({...arg, filterMatchMode: arg.nameMatchMode})}`,
        method: 'GET',
      }),
      providesTags: (result) => {
        if (result != null) {
          return [
            {type: 'Biotwins', id: 'LIST'},
            ...result.items.map((biotwin) => ({type: 'Biotwins' as const, id: biotwin.id})),
          ];
        } else {
          return [{type: 'Biotwins', id: 'LIST'}];
        }
      },
    }),

    resetBiotwin: builder.mutation<Biotwin, {id: number}>({
      query: ({id}) => ({url: `/biotypes/${id}/reset`, method: 'DELETE'}),
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        try {
          await queryFulfilled;
          await dispatch(
            api.util.invalidateTags([
              {type: 'Biobook', id: arg.id},
              {type: 'BiobookChapters', id: arg.id},
              {type: 'BiobookCounts', id: arg.id},
              {type: 'Biotwins', id: 'LIST'},
              {type: 'BiotwinConditions', id: arg.id},
              {type: 'RelevantConditions', id: arg.id},
            ]),
          );
          await dispatch(api.util.invalidateTags([{type: 'BiotwinScores', id: arg.id}]));
        } catch (e) {}
      },
    }),

    updateBiotwin: builder.mutation<Biotwin, {id: number; changes: Partial<Biotwin>}>({
      query: ({id, changes}) => ({url: `/biotypes/${id}`, method: 'PATCH', body: changes}),
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        try {
          const {data: biotwin} = await queryFulfilled;
          if (biotwin != null) {
            dispatch(biotwinApi.util.updateQueryData('getBiotwin', {id: biotwin.id}, () => biotwin));
          }
          await dispatch(
            api.util.invalidateTags([
              {type: 'Biobook', id: arg.id},
              {type: 'BiobookChapters', id: arg.id},
              {type: 'BiobookCounts', id: arg.id},
              {type: 'Biotwins', id: 'LIST'},
              {type: 'BiotwinConditions', id: arg.id},
              {type: 'RelevantConditions', id: arg.id},
            ]),
          );
          await dispatch(api.util.invalidateTags([{type: 'BiotwinScores', id: arg.id}]));
        } catch (e) {}
      },
    }),

    // biotwin condition endpoints
    createBiotwinCondition: builder.mutation<
      unknown,
      {conditionId: number; drugId?: number; userId: number; value?: number}
    >({
      query: (arg) => ({url: `/biotype-conditions`, method: 'POST', body: arg}),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Biobook', id: arg.userId},
        {type: 'BiobookChapters', id: arg.userId},
        {type: 'BiobookCounts', id: arg.userId},
        {type: 'BiotwinConditions', id: arg.userId},
        {type: 'BiotwinScores', id: arg.userId},
        {type: 'RelevantConditions', id: arg.userId},
      ],
    }),

    deleteBiotwinCondition: builder.mutation<unknown, {id: BiotwinCondition['id']; userId: number}>({
      query: ({id, userId}) => ({url: `/biotype-conditions/${id}?userId=${userId}`, method: 'DELETE'}),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Biobook', id: arg.userId},
        {type: 'BiobookChapters', id: arg.userId},
        {type: 'BiobookCounts', id: arg.userId},
        {type: 'BiotwinConditions', id: arg.userId},
        {type: 'BiotwinScores', id: arg.userId},
        {type: 'RelevantConditions', id: arg.userId},
      ],
    }),

    getBiotwinConditions: builder.query<BiotwinCondition[], {userId: number}>({
      query: ({userId}) => ({url: `/biotype-conditions?userId=${userId}&pageNumber=1&pageSize=150`, method: 'GET'}),
      providesTags: (_result, _error, arg) => [{type: 'BiotwinConditions', id: arg.userId}],
    }),

    updateBiotwinCondition: builder.mutation<
      unknown,
      {id: BiotwinCondition['id']; userId: number} & Partial<BiotwinCondition>
    >({
      query: ({id, userId, ...changes}) => ({
        url: `/biotype-conditions/${id}`,
        method: 'PATCH',
        body: {...changes, userId},
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Biobook', id: arg.userId},
        {type: 'BiobookChapters', id: arg.userId},
        {type: 'BiobookCounts', id: arg.userId},
        {type: 'BiotwinConditions', id: arg.userId},
        {type: 'BiotwinScores', id: arg.userId},
        {type: 'RelevantConditions', id: arg.userId},
      ],
    }),

    updateBulkBiotwinConditions: builder.mutation<
      unknown,
      {added?: {conditionId: number; drugId?: number; value?: number}[]; removedIds?: number[]; userId: number}
    >({
      query: (arg) => ({url: `/biotype-conditions`, method: 'PATCH', body: arg}),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Biobook', id: arg.userId},
        {type: 'BiobookChapters', id: arg.userId},
        {type: 'BiobookCounts', id: arg.userId},
        {type: 'BiotwinConditions', id: arg.userId},
        {type: 'BiotwinScores', id: arg.userId},
        {type: 'RelevantConditions', id: arg.userId},
      ],
    }),

    // relevant condition endpoints
    getRelevantConditions: builder.query<RelevantCondition[], {userId: number}>({
      query: ({userId}) => ({
        url: `/relevant-conditions?userId=${userId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{type: 'RelevantConditions', id: arg.userId}],
      transformResponse: (response: RelevantCondition[]) => {
        return (
          response
            .slice()
            .sort(
              (a, b) =>
                Number(a.showOrder ?? Number.MAX_SAFE_INTEGER) - Number(b.showOrder ?? Number.MAX_SAFE_INTEGER) ||
                a.conditionName.localeCompare(b.conditionName),
            ) ?? []
        );
      },
    }),

    // biotwin conditions history
    getBiotwinConditionsHistory: builder.query<BiotwinHistoryItem[], {from?: string; to?: string; userId: number}>({
      query: ({
        from = '1900-01-01T00:00:00.000Z',
        to = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        userId,
      }) => ({
        url: `/biotype-conditions/history${buildURLSearch({from, to, userId})}`,
        method: 'GET',
      }),
    }),

    // biotwin score endpoints
    getBiotwinScores: builder.query<BiotwinScore[], {userId: number}>({
      query: ({userId}) => ({url: `/biotype-scores?userId=${userId}`, method: 'GET'}),
      providesTags: (_result, _error, arg) => [{type: 'BiotwinScores', id: arg.userId}],
    }),

    // summary endpoints
    askAI: builder.query<
      AIResult,
      {
        bioTypeName?: string;
        language?: string;
        prompt?: string;
        systemPrompt?: string;
        strictness?: number | null;
        type?: number;
        userId: number;
      }
    >({
      query: ({bioTypeName, language, prompt, systemPrompt, strictness, type = 0, userId}) => ({
        url: `/biotypes/${userId}/ask-ai`,
        method: 'POST',
        body: {
          bioTypeName,
          language,
          prompt: prompt || undefined,
          systemPrompt: systemPrompt || undefined,
          strictness,
          type,
        },
      }),
      transformResponse: (response: string | AIResult) => {
        return typeof response === 'string' ? {phraseIds: [], result: response} : response;
      },
    }),

    getAIQuestions: builder.query<AIQuestion[], void>({
      query: () => ({url: `/biotypes/ai-questions`, method: 'GET'}),
    }),

    askAIQuestion: builder.query<AIResult, {questionId: number; userId: number}>({
      query: ({questionId, userId}) => ({
        url: `/biotypes/${userId}/ask-ai/${questionId}`,
        method: 'POST',
      }),
      transformResponse: (response: string | AIResult) => {
        if (typeof response === 'string') {
          return {phraseIds: [], result: response};
        }
        if (response && typeof response === 'object') {
          if ('result' in response && typeof response.result === 'string') {
            return response as AIResult;
          }
          if ('answer' in response && typeof response.answer === 'string') {
            return {phraseIds: [], result: (response as any).answer};
          }
        }
        return {phraseIds: [], result: ''};
      },
    }),
  }),
});

export const {
  useCheckBiotwinCalculationQuery,

  useCreateBiotwinMutation,
  useDeleteBiotwinMutation,
  useDuplicateBiotwinMutation,
  useGetBiotwinQuery,
  useGetBiotwinsQuery,
  useLazyGetBiotwinQuery,
  useLazyGetBiotwinsQuery,
  useResetBiotwinMutation,
  useUpdateBiotwinMutation,

  useCreateBiotwinConditionMutation,
  useDeleteBiotwinConditionMutation,
  useGetBiotwinConditionsQuery,
  useLazyGetBiotwinConditionsQuery,
  useUpdateBiotwinConditionMutation,
  useUpdateBulkBiotwinConditionsMutation,

  useGetBiotwinScoresQuery,

  useGetBiotwinConditionsHistoryQuery,

  useGetRelevantConditionsQuery,

  useLazyGetRelevantConditionsQuery,

  useLazyAskAIQuery,

  useGetAIQuestionsQuery,
  useLazyAskAIQuestionQuery,
} = biotwinApi;
